import { createContext, useContext, useState, ReactNode } from 'react';
import type { ResellerCompany } from '@/types/reseller';

interface ResellerPreviewContextType {
  previewCompany: ResellerCompany | null;
  setPreviewCompany: (company: ResellerCompany | null) => void;
  isPreviewMode: boolean;
  getDiscountedPrice: (price: number) => number;
}

const ResellerPreviewContext = createContext<ResellerPreviewContextType | undefined>(undefined);

export function ResellerPreviewProvider({ children }: { children: ReactNode }) {
  const [previewCompany, setPreviewCompany] = useState<ResellerCompany | null>(null);

  const isPreviewMode = previewCompany !== null;

  const getDiscountedPrice = (price: number): number => {
    if (!previewCompany || !previewCompany.discount_percentage) {
      return price;
    }
    const discount = previewCompany.discount_percentage / 100;
    return Math.round(price * (1 - discount));
  };

  return (
    <ResellerPreviewContext.Provider
      value={{
        previewCompany,
        setPreviewCompany,
        isPreviewMode,
        getDiscountedPrice,
      }}
    >
      {children}
    </ResellerPreviewContext.Provider>
  );
}

export function useResellerPreview() {
  const context = useContext(ResellerPreviewContext);
  if (context === undefined) {
    throw new Error('useResellerPreview must be used within a ResellerPreviewProvider');
  }
  return context;
}
